import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  return (
    <main>
      <Navbar />
      <article className="pt-24 pb-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: (() => {
                const itMonths: Record<string, string> = {
                  Gennaio: '01', Febbraio: '02', Marzo: '03', Aprile: '04',
                  Maggio: '05', Giugno: '06', Luglio: '07', Agosto: '08',
                  Settembre: '09', Ottobre: '10', Novembre: '11', Dicembre: '12',
                };
                const parts = article.date.split(' ');
                const isoDate = parts.length === 3
                  ? `${parts[2]}-${itMonths[parts[1]] || '01'}-${parts[0].padStart(2, '0')}`
                  : '2026-07-14';
                return JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline: article.title,
                  description: article.summary,
                  image: article.image,
                  datePublished: isoDate,
                  dateModified: '2026-07-14',
                  author: {
                    '@type': 'Organization',
                    name: 'Italian Shopping Research Team',
                    url: 'https://itaobuyspread.work/',
                  },
                  publisher: {
                    '@type': 'Organization',
                    name: 'Itaobuy Spreadsheet Italia',
                    url: 'https://itaobuyspread.work/',
                  },
                  mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': `https://itaobuyspread.work/articoli/${article.slug}`,
                  },
                  inLanguage: 'it-IT',
                  isAccessibleForFree: true,
                });
              })(),
            }}
          />
          <nav className="mb-8 flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer">
              Home
            </Link>
            <span>/</span>
            <span className="text-foreground-400">Guide & Articoli</span>
          </nav>

          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Torna alla Home
          </Link>

          <header className="mb-10">
            <h1 className="text-foreground-950 font-heading text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-foreground-500 border-b border-background-200/60 pb-6">
              <span>
                <strong className="text-foreground-700">Scritto da:</strong> Italian Shopping Research Team
              </span>
              <span>
                <strong className="text-foreground-700">Pubblicato:</strong> {article.date}
              </span>
              <span>
                <strong className="text-foreground-700">Ultimo aggiornamento:</strong> 14 Luglio 2026
              </span>
              <span>
                <strong className="text-foreground-700">Tempo di lettura:</strong> {article.readingTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="bg-accent-50/50 border border-accent-200/40 rounded-2xl p-6 mb-10">
            <p className="text-sm font-semibold text-foreground-800 mb-2">Risposta Veloce</p>
            <p className="text-foreground-700 leading-relaxed">
              {article.summary}
            </p>
          </div>

          <div className="bg-background-100 rounded-2xl p-6 mb-10">
            <p className="text-sm font-semibold text-foreground-800 mb-4">Indice dei Contenuti</p>
            <ul className="space-y-2 text-sm text-foreground-600">
              {article.sections.map((section, idx) => (
                <li key={idx}>
                  <a href={`#section-${idx}`} className="hover:text-foreground-950 transition-colors cursor-pointer">
                    {section.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="prose-custom space-y-10 text-foreground-700 leading-relaxed">
            {article.sections.map((section, idx) => (
              <section key={idx} id={`section-${idx}`}>
                <h2 className="text-foreground-950 font-heading text-2xl font-bold mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className={pIdx > 0 ? 'mt-4' : ''}>
                    {p}
                  </p>
                ))}
                {section.list && section.list.length > 0 && (
                  section.isOrdered ? (
                    <ol className="list-decimal pl-5 space-y-2 mt-3">
                      {section.list.map((item, lIdx) => (
                        <li key={lIdx}>{item}</li>
                      ))}
                    </ol>
                  ) : (
                    <ul className="list-disc pl-5 space-y-2 mt-3">
                      {section.list.map((item, lIdx) => (
                        <li key={lIdx}>{item}</li>
                      ))}
                    </ul>
                  )
                )}
              </section>
            ))}

            {article.faqs && article.faqs.length > 0 && (
              <section>
                <h2 className="text-foreground-950 font-heading text-2xl font-bold mb-4">
                  Domande Frequenti
                </h2>
                <div className="space-y-6">
                  {article.faqs.map((faq, fIdx) => (
                    <div key={fIdx}>
                      <h3 className="text-foreground-950 font-semibold mb-2">
                        {faq.question}
                      </h3>
                      <p>{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <p className="text-sm text-foreground-500 mb-4">
              <strong className="text-foreground-700">Fonti e Riferimenti:</strong> Italian Shopping Research Team, Database Itaobuy Spreadsheet, Feedback Community Italiana
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 p-6 bg-background-100 rounded-2xl">
            <div className="flex-1">
              <p className="text-foreground-950 font-heading text-lg font-semibold">
                Pronto a scoprire Itaobuy Sheet?
              </p>
              <p className="text-foreground-500 text-sm">
                Accedi al database completo e inizia a fare shopping in modo intelligente.
              </p>
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              ← Torna alla Home Page
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}